import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Trash2, Edit, Copy } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { EmailTemplateEditor } from './EmailTemplateEditor';
import { captureError } from '@/lib/sentry';
import type { EmailTemplate } from '@/lib/email/template-utils';

export function EmailTemplateList() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  const selectedTagId = searchParams.get('tag');

  const handleTagClick = (tagId: string) => {
    setSearchParams(prev => {
      if (tagId === selectedTagId) {
        prev.delete('tag');
      } else {
        prev.set('tag', tagId);
      }
      return prev;
    });
  };

  const clearTagFilter = () => {
    setSearchParams(prev => {
      prev.delete('tag');
      return prev;
    });
  };

  const fetchData = useCallback(async () => {
    if (!currentWorkspace) return;

    try {
      setLoading(true);
      
      // Fetch all templates with their tag relations
      const { data: templatesData, error: templatesError } = await supabase
        .from('email_templates')
        .select(`
          *,
          template_tag_relations (
            tag_id,
            template_tags (
              id,
              name
            )
          )
        `)
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });

      if (templatesError) throw templatesError;

      // Filter templates by selected tag if one is selected
      let filteredTemplates = templatesData || [];
      if (selectedTagId) {
        filteredTemplates = filteredTemplates.filter(template => 
          template.template_tag_relations?.some((relation: any) => 
            relation.tag_id === selectedTagId
          )
        );
      }

      // Fetch tags
      const { error: tagsError } = await supabase
        .from('template_tags')
        .select('*')
        .eq('workspace_id', currentWorkspace.id);

      if (tagsError) throw tagsError;

      setTemplates(filteredTemplates);
      // setTags(tagsData); // Commented out as setTags was removed
    } catch (error) {
      console.error('Error fetching templates:', error);
      captureError(error as Error, { feature: 'email-template-fetch' });
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, selectedTagId, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, selectedTagId]);

  const deleteTemplate = async (id: string) => {
    try {
      await supabase.from('email_templates').delete().eq('id', id);
      await fetchData();

      toast({
        description: "Template deleted successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'templates-email-template-list-delete-template' });
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive",
      });
    }
  };

  const duplicateTemplate = async (template: EmailTemplate) => {
    if (isDuplicating) return; // guard: duplicate is not idempotent — ignore rapid double-clicks
    setIsDuplicating(true);
    try {
      // Insert the copy — mirrors EmailTemplateEditor's create branch.
      // workspace_id comes from the source row (list is RLS-filtered to the
      // current workspace), so no workspace context is needed here.
      const { data, error } = await supabase
        .from('email_templates')
        .insert({
          name: `${template.name} - copy`,
          subject: template.subject,
          body: template.body,
          variables: template.variables,
          workspace_id: template.workspace_id,
        })
        .select()
        .single();

      if (error) throw error;

      // Copy the source template's tag assignments onto the new copy.
      // Best-effort: the copy row is already committed, so a tag-copy failure
      // must NOT orphan-and-hide it or report a total failure. Report the
      // partial result instead and still open the copy for editing.
      const relations = (template.template_tag_relations ?? []).map(relation => ({
        template_id: data.id,
        tag_id: relation.tag_id,
      }));
      let tagsCopied = true;
      if (relations.length > 0) {
        const { error: tagError } = await supabase
          .from('template_tag_relations')
          .insert(relations);
        if (tagError) {
          tagsCopied = false;
          captureError(tagError as Error, { feature: 'duplicate-template-tags' });
        }
      }

      await fetchData();

      // Open the editor on the saved copy so the user can edit from there.
      setSelectedTemplate(data as EmailTemplate);
      setShowEditor(true);

      toast(
        tagsCopied
          ? { description: 'Template duplicated' }
          : {
              title: 'Copied without tags',
              description: 'The template was duplicated, but its tags could not be copied.',
              variant: 'destructive',
            },
      );
    } catch (error) {
      captureError(error as Error, { feature: 'duplicate-template' });
      toast({
        title: 'Error',
        description: 'Failed to duplicate template',
        variant: 'destructive',
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No workspace selected
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold tracking-tight">Email Templates</h2>
        <div className="flex items-center gap-2">
          {selectedTagId && (
            <Button 
              variant="outline"
              size="sm"
              onClick={clearTagFilter}
            >
              Clear tag filter
            </Button>
          )}
          <Button onClick={() => {
            setSelectedTemplate(null);
            setShowEditor(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading templates...</div>
      ) : templates.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Variables</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell className="font-medium">
                  {template.name}
                </TableCell>
                <TableCell>{template.subject}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {template.template_tag_relations?.map(relation => (
                      <Badge 
                        key={relation.tag_id}
                        variant="secondary"
                        className="cursor-pointer hover:bg-accent"
                        onClick={() => handleTagClick(relation.tag_id)}
                      >
                        {relation.template_tags.name}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {template.variables.map(v => v.name).join(', ')}
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {template.created_at ? format(new Date(template.created_at), 'MMM d, yyyy') : '—'}
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {template.updated_at ? format(new Date(template.updated_at), 'MMM d, yyyy') : '—'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowEditor(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => duplicateTemplate(template)}
                      disabled={isDuplicating}
                      title="Duplicate template"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteTemplate(template.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <p>No templates yet. Create your first template to get started.</p>
        </div>
      )}

      <EmailTemplateEditor
        open={showEditor}
        onOpenChange={setShowEditor}
        template={selectedTemplate}
        onSuccess={() => {
          setShowEditor(false);
          fetchData();
        }}
      />
    </div>
  );
}